import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import BackButton from 'src/components/BackButton';
import Iconify from 'src/components/Iconify';
import InfoAlert from 'src/components/InfoAlert';
import SuccessButton from 'src/components/SuccessButton';
import WarningAlert from 'src/components/WarningAlert';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import { USER_QUERY_KEY } from 'src/constants/query';
import useQueryData from 'src/queries/useQueryData';
import { userEmailSelector } from 'src/selectors/user';
import { buildDefaultStatusNote } from './defaultStatusNote';
import { formatDate } from 'src/utils/formatTime';
import { EDIT_STATUS_NOTE_INFO_LINK } from 'src/pages/wdac-results/tabs/Win32Apps/constants';

// Every status form opens the same way, whether it saves one app or many.
export const UPDATE_TRUST_STATUS_TITLE = 'Update Trust Status';
export const UPDATE_TRUST_STATUS_ICON = 'mdi:shield-check-outline';

export const statusSchema = Yup.object().shape({
  status: Yup.string().required('Status is required'),
  note: Yup.string(),
});

// Setting a status is a record of intent, not an enforcement change, so the tooltip names the
// artifact that still has to be regenerated and deployed before devices behave differently. A form
// that says as much itself leaves the catalog target out and goes without the tooltip.
export const StatusDialog = ({
  open,
  onClose,
  title = UPDATE_TRUST_STATUS_TITLE,
  icon = UPDATE_TRUST_STATUS_ICON,
  summary,
  currentDate,
  lastGeneratedDate,
  catalogTarget,
  maxWidth = 'sm',
  children,
}) => {
  const contextLine = [
    summary,
    currentDate ? `Last updated ${formatDate(currentDate)}` : null,
    lastGeneratedDate ? `Last generated ${formatDate(lastGeneratedDate)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon ? <Iconify icon={icon} width={22} height={22} color="#87C8E0" /> : null}
        {title}
        {catalogTarget ? (
          <Tooltip
            arrow
            placement="right"
            componentsProps={{ tooltip: { sx: { maxWidth: 500, pointerEvents: 'auto' } } }}
            title={
              <Typography variant="body2" mb={1}>
                {`Setting the status to Trusted records that decision in AppControl, but it does not change what your devices allow on its own. You still need to update ${catalogTarget} and deploy it before the trust decision takes effect. `}
                <Link
                  href={EDIT_STATUS_NOTE_INFO_LINK}
                  target="_blank"
                  rel="noopener"
                  underline="hover">
                  More info
                </Link>
              </Typography>
            }>
            <IconButton size="small">
              <Iconify icon="material-symbols:info-outline" width={24} height={24} />
            </IconButton>
          </Tooltip>
        ) : null}
      </DialogTitle>

      <DialogContent>
        {contextLine ? (
          <Typography variant="body2" sx={{ color: '#ccc', mb: 2, wordBreak: 'break-word' }}>
            {contextLine}
          </Typography>
        ) : null}
        {children}
      </DialogContent>
    </Dialog>
  );
};

// The status and note every status form has, with room beneath the note for a form that carries
// more. The caller owns the form, so its values outlive the dialog being swapped out. Saving waits
// for a change unless the caller decides when it can save itself.
export const StatusFormLayout = ({
  formMethods,
  statusOptions,
  infoAlert,
  warningAlert = null,
  disableStatusSelect,
  canSubmit,
  submitLabel = 'Save',
  loading,
  onCancel,
  onSubmit,
  children,
}) => {
  const {
    handleSubmit,
    watch,
    formState: { isDirty, isValid },
  } = formMethods;

  // A page can word the alerts for the status being chosen rather than the one already saved.
  const selectedStatus = watch('status');
  const forStatus = alert => (typeof alert === 'function' ? alert(selectedStatus) : alert);
  const alertText = forStatus(infoAlert);
  const warningText = forStatus(warningAlert);

  return (
    <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        {alertText ? (
          <InfoAlert icon={<Iconify icon="material-symbols:info-outline" width={20} height={20} />}>
            <Typography variant="body2">{alertText}</Typography>
          </InfoAlert>
        ) : null}
        {warningText ? (
          <WarningAlert>
            <Typography variant="body2">{warningText}</Typography>
          </WarningAlert>
        ) : null}
        <HookFormTextField
          name="status"
          label="Status"
          formMethods={formMethods}
          select
          size="small"
          fullWidth
          disabled={disableStatusSelect}
          options={statusOptions}
        />
        <HookFormTextField
          name="note"
          label="Note"
          formMethods={formMethods}
          placeholder="Enter note"
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
        />
        {children}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BackButton disabled={loading} onClick={onCancel}>
            Close
          </BackButton>
          <SuccessButton
            type="submit"
            disabled={loading || !isValid || !(canSubmit ?? isDirty)}
            loading={loading}>
            {submitLabel}
          </SuccessButton>
        </Stack>
      </Stack>
    </form>
  );
};

const UpdateStatusModal = ({
  open,
  onClose,
  onSave,
  loading,
  count = 1,
  noun = 'app',
  summary,
  currentStatus,
  currentNote,
  currentDate,
  statusOptions,
  disableStatusSelect = false,
  catalogTarget,
  infoAlert = null,
  warningAlert = null,
  prefillNote = false,
}) => {
  const isBulk = count > 1;
  const email = useQueryData(USER_QUERY_KEY, userEmailSelector);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { status: '', note: '' },
    resolver: yupResolver(statusSchema),
  });
  const { reset } = formMethods;

  // Each opening starts from what is saved. A prefilled note replaces the current one in the field,
  // so saving records who made this change.
  useEffect(() => {
    if (open) {
      reset({
        status: isBulk ? '' : currentStatus || '',
        note: prefillNote ? buildDefaultStatusNote(email) : isBulk ? '' : currentNote || '',
      });
    }
  }, [open, isBulk, currentStatus, currentNote, prefillNote, email, reset]);

  return (
    <StatusDialog
      open={open}
      onClose={onClose}
      title={
        isBulk ? `${UPDATE_TRUST_STATUS_TITLE} (${count} ${noun}s)` : UPDATE_TRUST_STATUS_TITLE
      }
      summary={summary}
      currentDate={isBulk ? null : currentDate}
      catalogTarget={catalogTarget}>
      <StatusFormLayout
        formMethods={formMethods}
        statusOptions={statusOptions}
        infoAlert={infoAlert}
        warningAlert={warningAlert}
        disableStatusSelect={disableStatusSelect}
        loading={loading}
        onCancel={onClose}
        onSubmit={values => onSave(values.status, values.note?.trim() || '')}
      />
    </StatusDialog>
  );
};

export default UpdateStatusModal;
